import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionHeading } from '../ui/Panels';
import { chefScenes, guestScenes } from './StepScenes';
import { cn } from '../../utils/cn';
import { spring, viewportOnce } from '../../utils/motion';
const guestSteps = [{
  title: 'Say what the night is',
  body: 'Date, head count, and whether you want a chef in your kitchen or food at your door. Pick one of their menus or describe your own with a budget.',
  aside: 'Your dietary record attaches itself — you never retype an allergy.'
}, {
  title: 'Get a real quote, in chat',
  body: 'The chef replies with an itemised price: menu × guests, hours beyond the base, groceries, travel. You can ask for changes and they re-send a new version.',
  aside: 'Nothing appears at checkout that was not on the quote.'
}, {
  title: 'Accept and hold the date',
  body: 'A deposit confirms it and the balance is charged after service. Grocery receipts land in the booking the same night when they are billed at cost.',
  aside: 'Cancellation terms are shown before you commit, not after.'
}, {
  title: 'Eat, then say how it went',
  body: 'Rate food, punctuality, cleanliness and communication separately, so the next guest reading a 4.8 knows which part it refers to.',
  aside: 'Only guests who completed a booking can score a chef.'
}];
const chefSteps = [{
  title: 'Publish menus, not an hourly rate',
  body: 'Set a price per guest, a minimum head count, a minimum order, the hours included, and how groceries work. Add-ons stay separate and visible.',
  aside: 'Search compares you on “from ฿X / guest”, so nobody undercuts you with a hidden hourly.'
}, {
  title: 'Draw your service areas',
  body: 'Where you will cook, where you will only deliver, and how far you travel before per-kilometre charges start. Delivery gets its own radius and fee.',
  aside: 'Requests outside your areas never reach your inbox.'
}, {
  title: 'Quote from the menu',
  body: 'Pick a menu, set guests and hours, tick add-ons. The builder totals it, shows your take-home, and checks the menu against the guest’s kitchen.',
  aside: 'You must acknowledge the dietary record before a quote can send.'
}, {
  title: 'Get paid on a schedule',
  body: 'Deposit on confirmation, balance after service, payouts twice a month. The 10% fee is shown on every line rather than buried in a summary.',
  aside: 'We produce the annual summary; the tax return stays yours.'
}];
function Step({
  index,
  step,
  Scene
}: {
  index: number;
  step: {
    title: string;
    body: string;
    aside: string;
  };
  Scene: React.ComponentType;
}) {
  const reduce = useReducedMotion();
  const flip = index % 2 === 1;
  return <li className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16" data-id="element-550" data-mp="ms8egl2t895j">
      <motion.div initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      x: flip ? 28 : -28
    }} whileInView={{
      opacity: 1,
      x: 0
    }} viewport={viewportOnce} transition={reduce ? {
      duration: 0.2
    } : spring.smooth} className={cn(flip && 'lg:order-2')} data-id="element-551" data-mp="rqjp2y0xinot">
        <div className="flex items-baseline gap-3" data-id="element-552" data-mp="f64kn0baipn2">
          <span className="tnum font-display text-[clamp(2.6rem,5vw,4rem)] font-semibold leading-none tracking-[-0.04em] text-action/25" data-id="element-553" data-mp="a7tccu0n8n6y">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="mb-1.5 h-px flex-1 bg-hairline" aria-hidden="true" data-id="element-554" data-mp="18y4zelv5722" />
        </div>
        <h3 className="mt-4 max-w-[22ch] font-display text-[clamp(1.5rem,2.6vw,2.1rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-ink" data-id="element-555" data-mp="3slsppc1xcem0">
          {step.title}
        </h3>
        <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-ink-2" data-id="element-556" data-mp="3ip65mphf9rl1">{step.body}</p>
        <p className="mt-4 max-w-[46ch] border-l-2 border-primary/30 pl-4 text-[13px] leading-relaxed text-ink-3" data-id="element-557" data-mp="3enko2qty0p4i">{step.aside}</p>
      </motion.div>

      <motion.div initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      x: flip ? -24 : 24,
      scale: 0.98
    }} whileInView={{
      opacity: 1,
      x: 0,
      scale: 1
    }} viewport={viewportOnce} transition={reduce ? {
      duration: 0.2
    } : spring.heavy} className={cn(flip && 'lg:order-1')} data-id="element-558" data-mp="39orjxp0jqah0">
        <Scene data-id="element-559" data-mp="13sxkjoh3gyge" />
      </motion.div>
    </li>;
}
export function HowItWorks() {
  const [side, setSide] = useState<'guest' | 'chef'>('guest');
  const steps = side === 'guest' ? guestSteps : chefSteps;
  const scenes = side === 'guest' ? guestScenes : chefScenes;
  return <section aria-labelledby="how-heading" className="border-y border-hairline bg-canvas" data-id="element-560" data-mp="yxi7uzutp8lk">
      <div className="mx-auto max-w-shell px-5 py-20 lg:px-8 lg:py-24" data-id="element-561" data-mp="kxh67i6qhx51">
        <SectionHeading level={2} title="How a booking actually goes" hint="Two sides of the same conversation. Nothing here is a black box." action={<div className="flex gap-1 rounded-full bg-oat p-1" role="tablist" aria-label="Point of view" data-id="element-563" data-mp="2k9tfog0ekyoe">
              {(['guest', 'chef'] as const).map(option => <button key={option} role="tab" type="button" aria-selected={side === option} onClick={() => setSide(option)} className={cn('rounded-full px-4 py-2 text-[13px] font-semibold transition-colors duration-150 ease-out', side === option ? 'bg-surface text-ink shadow-lift' : 'text-ink-2 hover:text-ink')} data-id="element-564" data-mp="2pe3z7fwo453s">
                  {option === 'guest' ? "I'm booking" : "I'm cooking"}
                </button>)}
            </div>} data-id="element-562" data-mp="puh435tfjtl2" />

        <ol key={side} className="mt-14 space-y-20 lg:space-y-28" data-id="element-565" data-mp="3i447loqdo0be">
          {steps.map((step, i) => <Step key={`${side}-${step.title}`} index={i} step={step} Scene={scenes[i]} data-id="element-566" data-mp="1rqvjk1jclq9p" data-mpi={i} />)}
        </ol>
      </div>
    </section>;
}